/**
 * Replaces only the content generated by the previous handoff. Reader-authored text stays intact,
 * including edits to a generated prompt after it was inserted.
 */
export function replaceGeneratedHandoffPrompt(
  existing: string,
  previousGenerated: string | undefined,
  incoming: string,
): string {
  if (existing.trim().length === 0) return incoming;
  const previous = previousGenerated ?? "";
  const preserved =
    previous.length === 0
      ? existing
      : existing === previous
        ? ""
        : existing.endsWith(`\n\n${previous}`)
          ? existing.slice(0, -(previous.length + 2))
          : existing;
  if (preserved.trim().length === 0) return incoming;
  return incoming.length === 0 ? preserved : `${preserved}\n\n${incoming}`;
}

/**
 * Executes the failure-sensitive portion of a new-thread handoff. The draft identity exists
 * before checkout so setup belongs to a thread; composer content is written only after the same
 * draft has been pointed at the prepared checkout.
 */
export async function executePullRequestCheckoutTransaction<TDraft, TPrepared>(input: {
  readonly openDraft: () => Promise<TDraft>;
  readonly prepareCheckout: (draft: TDraft) => Promise<TPrepared>;
  readonly repointDraft: (draft: TDraft, prepared: TPrepared) => Promise<void>;
  readonly writeUnsent: ((draft: TDraft) => void) | null;
}): Promise<{ readonly draft: TDraft; readonly prepared: TPrepared }> {
  const draft = await input.openDraft();
  const prepared = await input.prepareCheckout(draft);
  await input.repointDraft(draft, prepared);
  input.writeUnsent?.(draft);
  return { draft, prepared };
}
export type PullRequestHandoffAction =
  | "ask"
  | "explain"
  | "fix"
  | "fix-new"
  | "fix-finding"
  | "resolve-conflicts"
  | "checkout-local"
  | "checkout-worktree"
  | "selection"
  | "transfer";

export type PullRequestHandoffFinding =
  | {
      readonly kind: "check";
      readonly check: PullRequestCheck;
    }
  | {
      readonly kind: "thread";
      readonly thread: PullRequestReviewThread;
    }
  | {
      readonly kind: "remark";
      readonly remark: PullRequestComment;
    };
import type {
  PullRequestCheck,
  PullRequestComment,
  PullRequestReviewThread,
} from "@cmmd-forge/contracts";
